import React, { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
    DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
    DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Heart, MessageCircleHeart, TrendingUp, Sparkles, Wind, BookHeart, LogOut, Menu, X, Home, UserCog, ShieldCheck } from "lucide-react";
import ProfilePromptDialog from "@/components/ProfilePromptDialog";

const nav = [
    { to: "/app", label: "Home", icon: Home, end: true },
    { to: "/app/chat", label: "Companion", icon: MessageCircleHeart },
    { to: "/app/moods", label: "Mood tracker", icon: TrendingUp },
    { to: "/app/affirmations", label: "Affirmations", icon: Sparkles },
    { to: "/app/breathing", label: "Breathing", icon: Wind },
    { to: "/app/favorites", label: "Favorites", icon: BookHeart },
];

export default function AppLayout() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [mobileOpen, setMobileOpen] = useState(false);

    const initials = (user?.name || user?.email || "?").split(" ").map(s => s[0]).slice(0, 2).join("").toUpperCase();

    const handleLogout = async () => {
        navigate("/", { replace: true });
        await logout();
    };

    return (
        <div className="min-h-screen bg-background flex">
            {/* Desktop sidebar */}
            <aside className="hidden lg:flex flex-col w-64 border-r border-border bg-card/50 backdrop-blur-xl">
                <div className="p-6 flex items-center gap-2">
                    <div className="w-9 h-9 rounded-2xl bg-primary flex items-center justify-center shadow-soft">
                        <Heart className="w-5 h-5 text-primary-foreground" strokeWidth={2} fill="currentColor" />
                    </div>
                    <span className="font-heading text-xl font-[800] tracking-tight">CheerUp!</span>
                </div>
                <nav className="flex-1 px-4 py-4 space-y-1">
                    {nav.map(item => (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            end={item.end}
                            data-testid={`sidebar-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                            className={({ isActive }) =>
                                `flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-medium transition-colors duration-200 ${
                                    isActive
                                        ? "bg-primary/90 text-primary-foreground shadow-soft"
                                        : "text-foreground/70 hover:bg-accent hover:text-foreground"
                                }`
                            }
                        >
                            <item.icon className="w-5 h-5" strokeWidth={1.5} />
                            {item.label}
                        </NavLink>
                    ))}
                </nav>
                <div className="p-4 border-t border-border">
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <button className="w-full flex items-center gap-3 p-3 rounded-2xl hover:bg-accent transition-colors duration-200" data-testid="user-menu-trigger">
                                <Avatar className="w-9 h-9">
                                    <AvatarImage src={user?.picture} />
                                    <AvatarFallback className="bg-primary/20 text-primary font-medium">{initials}</AvatarFallback>
                                </Avatar>
                                <div className="text-left flex-1 min-w-0">
                                    <div className="text-sm font-medium truncate">{user?.name || "Friend"}</div>
                                    <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
                                </div>
                            </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-56 rounded-2xl">
                            <DropdownMenuLabel>Signed in as</DropdownMenuLabel>
                            <DropdownMenuItem className="text-xs text-muted-foreground pointer-events-none">
                                {user?.email}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => navigate("/app/profile")} data-testid="profile-menu-item">
                                <UserCog className="w-4 h-4 mr-2" strokeWidth={1.5} />
                                Profile settings
                            </DropdownMenuItem>
                            {user?.is_admin && (
                                <DropdownMenuItem onClick={() => navigate("/app/admin")} data-testid="admin-menu-item">
                                    <ShieldCheck className="w-4 h-4 mr-2 text-primary" strokeWidth={1.5} />
                                    Admin dashboard
                                </DropdownMenuItem>
                            )}
                            <DropdownMenuItem onClick={handleLogout} data-testid="logout-menu-item">
                                <LogOut className="w-4 h-4 mr-2" strokeWidth={1.5} />
                                Sign out
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </aside>

            {/* Mobile top bar */}
            <div className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-background/80 backdrop-blur-xl border-b border-border">
                <div className="flex items-center justify-between px-5 py-4">
                    <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-2xl bg-primary flex items-center justify-center">
                            <Heart className="w-4 h-4 text-primary-foreground" strokeWidth={2} fill="currentColor" />
                        </div>
                        <span className="font-heading text-lg font-[800]">CheerUp!</span>
                    </div>
                    <Button variant="ghost" size="icon" onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-toggle">
                        {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                    </Button>
                </div>
                {mobileOpen && (
                    <nav className="px-4 pb-4 space-y-1">
                        {nav.map(item => (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                end={item.end}
                                onClick={() => setMobileOpen(false)}
                                data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                                className={({ isActive }) =>
                                    `flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-medium ${
                                        isActive ? "bg-primary text-primary-foreground" : "text-foreground/70 hover:bg-accent"
                                    }`
                                }
                            >
                                <item.icon className="w-5 h-5" strokeWidth={1.5} />
                                {item.label}
                            </NavLink>
                        ))}
                        <NavLink
                            to="/app/profile"
                            onClick={() => setMobileOpen(false)}
                            data-testid="mobile-nav-profile"
                            className={({ isActive }) =>
                                `flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-medium ${
                                    isActive ? "bg-primary text-primary-foreground" : "text-foreground/70 hover:bg-accent"
                                }`
                            }
                        >
                            <UserCog className="w-5 h-5" strokeWidth={1.5} />
                            Profile settings
                        </NavLink>
                        {user?.is_admin && (
                            <NavLink
                                to="/app/admin"
                                onClick={() => setMobileOpen(false)}
                                data-testid="mobile-nav-admin"
                                className={({ isActive }) =>
                                    `flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-medium ${
                                        isActive ? "bg-primary text-primary-foreground" : "text-foreground/70 hover:bg-accent"
                                    }`
                                }
                            >
                                <ShieldCheck className="w-5 h-5" strokeWidth={1.5} />
                                Admin dashboard
                            </NavLink>
                        )}
                        <button onClick={handleLogout} className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-medium text-foreground/70 hover:bg-accent" data-testid="mobile-logout">
                            <LogOut className="w-5 h-5" strokeWidth={1.5} />
                            Sign out
                        </button>
                    </nav>
                )}
            </div>

            {/* Content */}
            <main className="flex-1 min-w-0 lg:pt-0 pt-20">
                <Outlet />
            </main>
            <ProfilePromptDialog />
        </div>
    );
}
